import React from 'react';
import { TeamInLeague } from '../../domain/entities';
import './player-detail/HiddenStatsPanel.css';

interface OppositionTeamDebugPanelProps {
    team: TeamInLeague;
}

export const OppositionTeamDebugPanel: React.FC<OppositionTeamDebugPanelProps> = ({ team }) => {
    const getTacticsString = () => {
        return `${team.tactics.formation} (${team.tactics.mentality})`;
    };

    const countObjectives = (type: string) => {
        return team.seasonObjectives.filter(o => o.type === type).length;
    };

    return (
        <div className="panel hidden-stats-panel">
            <div className="panel-header">Opposition Debug Info 🔍</div>
            <div className="panel-content">
                <div className="hidden-stats-grid">
                    {/* Finances */}
                    <div className="hidden-stats-section">
                        <div className="hidden-stats-section-title">Finances</div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Balance</span>
                            <span className="hidden-stats-value">£{team.finances.balance.toLocaleString()}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">History Entries</span>
                            <span className="hidden-stats-value">{team.finances.history.length}</span>
                        </div>
                    </div>

                    {/* Facilities & Development */}
                    <div className="hidden-stats-section">
                        <div className="hidden-stats-section-title">Facilities & Development</div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Training Facilities</span>
                            <span className="hidden-stats-value">Level {team.trainingFacilities.level}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Youth Academy</span>
                            <span className="hidden-stats-value">Level {team.youthAcademy.level}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Youth Squad Size</span>
                            <span className="hidden-stats-value">{team.youthAcademy.squad.length}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Assistant Manager</span>
                            <span className="hidden-stats-value">Level {team.assistantManager.level}</span>
                        </div>
                    </div>

                    {/* Tactics */}
                    <div className="hidden-stats-section">
                        <div className="hidden-stats-section-title">Tactics</div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Formation</span>
                            <span className="hidden-stats-value">{getTacticsString()}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Attacking Focus</span>
                            <span className="hidden-stats-value">{team.tactics.attackingFocus}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Passing Style</span>
                            <span className="hidden-stats-value">{team.tactics.passingStyle}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Defensive Style</span>
                            <span className="hidden-stats-value">{team.tactics.defensiveStyle}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Tackling Style</span>
                            <span className="hidden-stats-value">{team.tactics.tacklingStyle}</span>
                        </div>
                    </div>

                    {/* Training Focus */}
                    <div className="hidden-stats-section">
                        <div className="hidden-stats-section-title">Training Focus</div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Team Focus</span>
                            <span className="hidden-stats-value">{team.trainingFocus}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">GK Focus</span>
                            <span className="hidden-stats-value">{team.positionalTrainingFocus.GK || 'Default'}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">DEF Focus</span>
                            <span className="hidden-stats-value">{team.positionalTrainingFocus.DEF || 'Default'}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">MID Focus</span>
                            <span className="hidden-stats-value">{team.positionalTrainingFocus.MID || 'Default'}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">FWD Focus</span>
                            <span className="hidden-stats-value">{team.positionalTrainingFocus.FWD || 'Default'}</span>
                        </div>
                    </div>

                    {/* Team Status */}
                    <div className="hidden-stats-section">
                        <div className="hidden-stats-section-title">Team Status</div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Board Confidence</span>
                            <span className="hidden-stats-value">{Math.round(team.boardConfidence * 100)}%</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Base Skill</span>
                            <span className="hidden-stats-value">{team.baseSkill}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Squad Size</span>
                            <span className="hidden-stats-value">{team.squad.length}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Position History Entries</span>
                            <span className="hidden-stats-value">{team.positionHistory.length}</span>
                        </div>
                    </div>

                    {/* Season Objectives */}
                    <div className="hidden-stats-section">
                        <div className="hidden-stats-section-title">Season Objectives</div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">League Objectives</span>
                            <span className="hidden-stats-value">{countObjectives('league')}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Cup Objectives</span>
                            <span className="hidden-stats-value">{countObjectives('cup')}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Finance Objectives</span>
                            <span className="hidden-stats-value">{countObjectives('finance')}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Challenge Objectives</span>
                            <span className="hidden-stats-value">{countObjectives('challenge')}</span>
                        </div>
                    </div>

                    {/* Club History Highlights */}
                    <div className="hidden-stats-section">
                        <div className="hidden-stats-section-title">Club History</div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Most Appearances</span>
                            <span className="hidden-stats-value">{team.clubHistory.mostAppearances.playerName} ({team.clubHistory.mostAppearances.value})</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Most Goals</span>
                            <span className="hidden-stats-value">{team.clubHistory.mostGoals.playerName} ({team.clubHistory.mostGoals.value})</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Record Transfer Fee</span>
                            <span className="hidden-stats-value">£{team.clubHistory.recordTransferFee.fee.toLocaleString()}</span>
                        </div>
                        <div className="hidden-stats-row">
                            <span className="hidden-stats-label">Retired Legends</span>
                            <span className="hidden-stats-value">{team.clubHistory.retiredLegends.length}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
